import React, { useState, useEffect, useCallback } from 'react';
import { quotationAPI, inventoryAPI } from '../services/api';
import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';
import { Loader2, Plus, Search, Download, Edit, Trash2, Calendar, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
// Success Orchestration Suite
import { Dialog, DialogContent } from './ui/dialog';
import SuccessScreen from './ui/SuccessScreen';
import CreateQuotationModal from './quotations/CreateQuotationModal';
import EditQuotationModal from './quotations/EditQuotationModal';

const QuotationPage = ({ onBack }) => {
    const [quotations, setQuotations] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedQuotation, setSelectedQuotation] = useState(null);

    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const pageSize = 10;
    const sortBy = 'quotationId';
    const sortDir = 'DESC';

    const [isSuccess, setIsSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [countdown, setCountdown] = useState(3);

    useEffect(() => {
        let timer;
        if (isSuccess && countdown > 0) {
            timer = setInterval(() => setCountdown(c => c - 1), 1000);
        } else if (isSuccess && countdown === 0) {
            setIsSuccess(false);
            setCountdown(3);
        }
        return () => clearInterval(timer);
    }, [isSuccess, countdown]);

    const loadQuotations = useCallback(async () => {
        try {
            setLoading(true);
            const params = {
                page: currentPage,
                size: pageSize,
                sortBy: sortBy,
                sortDir: sortDir
            };
            const response = await quotationAPI.getQuotations(params);
            setQuotations(response.data.content || []);
            setTotalElements(response.data.totalElements || 0);
            setTotalPages(response.data.totalPages || 0);
            setError(null);
        } catch (err) {
            console.error('Error loading quotations:', err);
            const errorMessage = err.response?.data?.message || err.message || 'Failed to load quotations. Please try again.';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, sortBy, sortDir]);

    const loadProducts = useCallback(async () => {
        try {
            const response = await inventoryAPI.getProducts({ limit: 1000 });
            setProducts(response.data.content || response.data.data || []);
        } catch (err) {
            console.error('Error loading products for quotations:', err);
        }
    }, []);

    // Zero-Refresh Real-time Registry Synchronizer
    useEffect(() => {
        const socketUrl = '/ws';
        const stompClient = new Client({
            webSocketFactory: () => new SockJS(socketUrl),
            debug: (str) => { /* console.log('Quotation Sync: ' + str); */ },
            reconnectDelay: 5000,
            heartbeatIncoming: 4000,
            heartbeatOutgoing: 4000,
        });

        stompClient.onConnect = (frame) => {
            console.log('Quotation Registry connected to live-sync: ' + frame);
            stompClient.subscribe('/topic/dashboard', (message) => {
                console.log('Quotation Registry broadcast received: ' + message.body);
                if (message.body === 'SYNC') {
                    loadQuotations(); // In-place refresh of registry
                }
            });
        };

        stompClient.onStompError = (frame) => {
            console.error('Quotation Sync Broker error: ' + frame.headers['message']);
        };

        stompClient.activate();

        return () => {
            if (stompClient) stompClient.deactivate();
        };
    }, [loadQuotations]);

    useEffect(() => {
        loadQuotations();
        loadProducts();
    }, [loadQuotations, loadProducts]);

    const handleCreateQuotation = async (quotationData) => {
        try {
            await quotationAPI.createQuotation(quotationData);
            setSuccessMessage('Quotation created successfully!');
            setIsSuccess(true);
            setShowCreateModal(false);
            loadQuotations();
        } catch (err) {
            console.error('Error creating quotation:', err);
            const errorMessage = err.response?.data?.message || err.message || 'Failed to create quotation';
            setError(errorMessage);
        }
    };

    const handleUpdateQuotation = async (quotationData) => {
        try {
            const id = selectedQuotation.quotationId;
            await quotationAPI.updateQuotation(id, quotationData);
            setSuccessMessage('Quotation updated successfully!');
            setIsSuccess(true);
            setShowEditModal(false);
            loadQuotations();
        } catch (err) {
            console.error('Error updating quotation:', err);
            const errorMessage = err.response?.data?.message || err.message || 'Failed to update quotation';
            setError(errorMessage);
        }
    };

    const handleDeleteQuotation = async (id) => {
        if (window.confirm('Are you sure you want to delete this quotation?')) {
            try {
                await quotationAPI.deleteQuotation(id);
                setSuccessMessage('Quotation deleted successfully!');
                setIsSuccess(true);
                loadQuotations();
            } catch (err) {
                console.error('Error deleting quotation:', err);
                const errorMessage = err.response?.data?.message || err.message || 'Failed to delete quotation';
                setError(errorMessage);
            }
        }
    };

    const handleDownloadPdf = async (id, num) => {
        try {
            const response = await quotationAPI.downloadQuotationPdf(id);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Quotation_${num || id}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            console.error('Error downloading quotation PDF:', err);
            alert('Failed to download quotation PDF');
        }
    };

    const filteredQuotations = quotations.filter(q =>
        q.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.quotationNumber?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={onBack}>
                        <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <h1 className="text-2xl font-bold tracking-tight">Quotations Registry</h1>
                </div>
                <Button onClick={() => setShowCreateModal(true)}>
                    <Plus className="mr-2 h-4 w-4" /> New Quotation
                </Button>
            </div>

            <Card>
                <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row gap-4 mb-6">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                            <Input
                                placeholder="Search by customer or quota number..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                            />
                        </div>
                    </div>

                    <div className="rounded-md border">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Quotation #</TableHead>
                                    <TableHead>Customer</TableHead>
                                    <TableHead>Date</TableHead>
                                    <TableHead>Amount</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center py-10">
                                            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
                                            <p className="mt-2 text-muted-foreground">Loading quotations...</p>
                                        </TableCell>
                                    </TableRow>
                                ) : filteredQuotations.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center py-10 text-muted-foreground">
                                            No quotations found.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredQuotations.map((quotation) => (
                                        <TableRow key={quotation.quotationId}>
                                            <TableCell className="font-medium">
                                                {quotation.quotationNumber}
                                            </TableCell>
                                            <TableCell>
                                                <div className="font-medium">{quotation.customerName}</div>
                                                <div className="text-xs text-muted-foreground">{quotation.customerPhone}</div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1">
                                                    <Calendar className="h-3 w-3" />
                                                    {quotation.quotationDate}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                ₹{(quotation.grandTotal || 0).toLocaleString()}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-2">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => handleDownloadPdf(quotation.quotationId, quotation.quotationNumber)}
                                                        title="Download PDF"
                                                    >
                                                        <Download className="h-4 w-4 text-blue-500" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => {
                                                            setSelectedQuotation(quotation);
                                                            setShowEditModal(true);
                                                        }}
                                                        title="Edit"
                                                    >
                                                        <Edit className="h-4 w-4 text-amber-500" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => handleDeleteQuotation(quotation.quotationId)}
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="h-4 w-4 text-red-500" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    <div className="flex items-center justify-between py-4">
                        <div className="text-sm text-muted-foreground">
                            Showing {filteredQuotations.length} of {totalElements} quotations
                        </div>
                        <div className="flex items-center space-x-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                                disabled={currentPage === 0 || loading}
                            >
                                <ChevronLeft className="h-4 w-4 mr-2" /> Previous
                            </Button>
                            <span className="text-sm font-medium">Page {currentPage + 1} of {totalPages || 1}</span>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(p => p + 1)}
                                disabled={(currentPage + 1) >= totalPages || loading}
                            >
                                Next <ChevronRight className="h-4 w-4 ml-2" />
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {error && (
                <div className="bg-destructive/15 text-destructive p-4 rounded-md flex items-center gap-3">
                    <AlertCircle className="h-5 w-5" />
                    <p>{error}</p>
                </div>
            )}

            <Dialog open={isSuccess} onOpenChange={setIsSuccess}>
                <DialogContent className="sm:max-w-md bg-transparent border-none shadow-none">
                    <SuccessScreen message={successMessage} countdown={countdown} />
                </DialogContent>
            </Dialog>

            {showCreateModal && (
                <CreateQuotationModal
                    open={showCreateModal}
                    onOpenChange={setShowCreateModal}
                    onSubmit={handleCreateQuotation}
                    products={products}
                />
            )}

            {showEditModal && (
                <EditQuotationModal
                    open={showEditModal}
                    onOpenChange={setShowEditModal}
                    onSubmit={handleUpdateQuotation}
                    products={products}
                    quotation={selectedQuotation}
                />
            )}
        </div>
    );
};

export default QuotationPage;
